import React, { useRef, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useFullScreenHandle } from 'react-full-screen'
import { PMTILES_PREF_KEY } from '../config/config'
import { PMTILES_URL } from '../const/general'
import { BASE_LAYER_LIST, BASE_LAYER_KEY } from '../const/map_config'
import { RISK_LEVEL_POINT } from '../const/color'
import { setChronologyLevelColor, setRiskColor } from '../func/color';
import { StyleFullScreen, MapWrapper } from '../style/map_components';
import MapFullscreenIcon from '../item/map_fullscreen_icon'
import MapSwitchLayerIcon from '../item/map_switch_layer_icon'
import MapSedimentLegendIcon from '../item/map_sediment_legend_icon'
import '../style/legend_map.css';
import bbox from '@turf/bbox';
import choropleth_city from '../data/city.json';
import choropleth_area from '../data/area.json';
import choropleth_msh from '../data/msh.json';

const sourceSetting = {
    "osm": {
        "type": "raster",
        "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
        "tileSize": 256,
        "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>"
    },
    "city": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/city/{z}/{x}/{y}.mvt`]
    },
    "area": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/area/{z}/{x}/{y}.mvt`]
    },
    "msh": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/msh/{z}/{x}/{y}.mvt`]
    }
};

// 背景地図の設定
const baseLayer = [
    {
        "id": "osm-tiles",
        "type": "raster",
        "source": "osm",
        "minzoom": 0,
        "maxzoom": 24
    }
];

// PMTilesから取得して表示、非表示を制御するレイヤ達
const otherLayers = [
    {
        "id": "city",
        "source": "city",
        "source-layer": "city",
        "type": "fill",
        "paint": {
            "fill-color": "#5c9fed",
            "fill-opacity": 0.8
        },
        "layout": {
            "visibility": "none" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "area",
        "source": "area",
        "source-layer": "area",
        "type": "fill",
        "paint": {
            "fill-color": "#5c9fed",
            "fill-opacity": 0.8
        },
        "layout": {
            "visibility": "none" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "msh",
        "source": "msh",
        "source-layer": "msh",
        "type": "fill",
        "paint": {
            "fill-color": "#5c9fed",
            "fill-opacity": 0.8
        },
        "layout": {
            "visibility": "none" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "city_line",
        "source": "city",
        "source-layer": "city",
        "type": "line",
        "paint": {
            "line-opacity": 0.8,
            "line-color": "#FFFFFF",
            "line-width": 1.5
        },
        "layout": {
            "visibility": "visible"
        }
    }
];

const MapComponent = (props) => {
    const mapContainer = useRef(null);
    const map = useRef(null);

    // 監視用Ref
    const selectCitysRef = useRef(props.selectCitys);
    const cityLandslideRiskH1Ref = useRef(props.cityLandslideRiskH1);
    const areaLandslideRiskH1Ref = useRef(props.areaLandslideRiskH1);
    const mshLandslideRiskH1Ref = useRef(props.mshLandslideRiskH1);
    const chronologyDataRef = useRef(props.chronologyList);
    

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto'
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer, ...otherLayers]

    let hoveredId = null

    const filterData = () => {
        switch (props.selectLayer.category) {
            case BASE_LAYER_KEY.CITY:
                return cityLandslideRiskH1Ref.current
            case BASE_LAYER_KEY.AREA:
                return areaLandslideRiskH1Ref.current
            case BASE_LAYER_KEY.MSH:
                return mshLandslideRiskH1Ref.current
            default:
                return cityLandslideRiskH1Ref.current
        }
    }

    // レイヤー切り替え
    const switchLayer = () => {
        if (map.current && map.current.isStyleLoaded()) {
            BASE_LAYER_LIST.map((obj) => {
                if (obj.category === props.selectLayer.category) {
                    map.current.setLayoutProperty(obj.category, 'visibility', 'visible');
                    map.current.on('mouseenter', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.on('mousemove', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.on('mouseleave', props.selectLayer.category, handleMouseLeave);
                } else {
                    map.current.setLayoutProperty(obj.category, 'visibility', 'none');
                    map.current.off('mouseenter', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.off('mousemove', props.selectLayer.category, (e) => { handleMouseMove(e) });
                    map.current.off('mouseleave', props.selectLayer.category, handleMouseLeave);
                }
            });
        }
    }

    // レイヤーの色分け
    const setLayerStyle = () => {
        if (!map.current || !map.current.isStyleLoaded()) return;
        switchLayer();

        // 対象のデータを選ぶ
        let riskData = filterData();

        if (props.selectSubCity.length > 0) {
            setMapCenterZoom(BASE_LAYER_KEY.CITY, props.selectSubCity);
        }
        if (props.selectArea.length > 0) {
            setMapCenterZoom(BASE_LAYER_KEY.AREA, props.selectArea);
        }
        if (props.selectMsh.length > 0) {
            setMapCenterZoom(BASE_LAYER_KEY.MSH, props.selectMsh);
        }

        const matchExpression_fill = ['match', ['get', 'id']];
        if (!riskData || riskData === undefined || riskData === null || riskData.length < 1) {
            map.current.setPaintProperty(props.selectLayer.category, 'fill-color', 'rgba(0,0,0,0)');
            // chronologyも消す
            if (map.current.getLayer('chronology')) {
                map.current.setLayoutProperty('chronology', 'visibility', 'none');
            }
            return;
        }
        for (const item of riskData) {
            let color = props?.setRiskColor ? props.setRiskColor(item.risk_val) : setRiskColor(RISK_LEVEL_POINT, item.risk_val);
            matchExpression_fill.push(item.code, color);
        }
        matchExpression_fill.push('rgba(0, 0, 0, 0)');

        map.current.setPaintProperty(props.selectLayer.category, 'fill-color', matchExpression_fill);

        // chronology
        if (chronologyDataRef.current && chronologyDataRef.current.length > 0) {
            // レイヤーがなければ追加
            if (!map.current.getLayer('chronology')) {
                addChronologyLayer();
            }
            // 表示/非表示を切り替え
            if (props.selectLayerList?.chronology) {
                map.current.setLayoutProperty('chronology', 'visibility', 'visible');
                // データ・色分けも更新
                const geo = buildChronologyGeoJSON(chronologyDataRef.current);
                map.current.getSource('chronology').setData(geo);
                setChronologyStyle();
            } else {
                map.current.setLayoutProperty('chronology', 'visibility', 'none');
            }
        } else {
            // データがなければ非表示
            if (map.current.getLayer('chronology')) {
                map.current.setLayoutProperty('chronology', 'visibility', 'none');
            }
        }

        // リセットされた場合
        if (props.selectSubCity.length === 0 && props.selectArea.length === 0 && props.selectMsh.length === 0) {
            setMapCenterZoom(props.selectLayer.category, riskData.map(data => data.code));
        }

    }

    // --- Map にクロノロジー用ソース & レイヤを追加 ---
    const addChronologyLayer = () => {
        if (!map.current.getSource('chronology')) {
            map.current.addSource('chronology', {
                type: 'geojson',
                data: { type: "FeatureCollection", features: [] }
            });
        }
        if (!map.current.getLayer('chronology')) {
            map.current.addLayer({
                id: 'chronology',
                type: 'circle',
                source: 'chronology',
                paint: {
                    'circle-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        5, 3,
                        10, 4,
                        12, 5,
                        15, 6,
                        18, 8
                    ],
                    'circle-stroke-width': 1.0,
                    'circle-stroke-color': '#ffffff',
                    'circle-color': '#0000FF',
                    'circle-opacity': 1
                },
                layout: {
                    visibility: (props.selectLayerList?.chronology ? 'visible' : 'none')
                }
            });
        }
        // クリック
        map.current.on('click', 'chronology', handleClickChronology);
        // ホバーでツールチップ（mousemove）とカーソル・離脱処理
        map.current.on('mousemove', 'chronology', handleMouseMoveChronology);
        map.current.on('mouseleave', 'chronology', handleMouseLeaveChronology);
        // カーソル切替
        map.current.on('mouseenter', 'chronology', () => {
            map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', 'chronology', () => {
            map.current.getCanvas().style.cursor = '';
        });
    };

    // マウスホバー時の処理
    const handleMouseMoveChronology = (e) => {
        const feature = e.features && e.features[0];
        if (!feature) return;
        const props = feature.properties || {};
        const id = props.code ?? props.id ?? feature.id;
        if (id === undefined || id === null) return;

        // すでにホバー中なら無視
        if (hoveredId === id) return;

        const chronologyData = chronologyDataRef.current;
        if (!Array.isArray(chronologyData) || chronologyData.length === 0) return;

        const data = chronologyData.find(item => item.code === Number(id) || item.code === id);

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }

        if (!data) {
            map.current.getCanvas().style.cursor = '';
            return;
        } else {
            map.current.getCanvas().style.cursor = 'pointer';
        }
        hoveredId = id;

        const damageName = data.TXT_MAIN_DAMAGE_NAME || data.txt_main_damage_name || '-';
        const dotColor = setChronologyLevelColor(damageName);

        const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false
        })
            .setLngLat(e.lngLat)
            .setHTML(`
                <div style="color:black;max-width:320px;">
                    <div style="font-weight:normal;display:flex;align-items:center;">
                        <div style="margin-right:6px;">主要な被害名：</div>
                        <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${dotColor};"></span>
                        <strong style="margin-left:6px;">${damageName}</strong>
                    </div>
                    <div>字名：<strong>${data.area_name || props.area_name || '-'}</strong></div>
                    <div>市町名：<strong>${data.city_name || props.city_name || '-'}</strong></div>
                    <div>更新日時（サマリ）：<strong>${data.TSM_RECEPT_DATE_SUM || data.tsm_recept_date_sum || '-'}</strong></div>
                </div>
            `)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeaveChronology = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null
        }
    };

    // レイヤークリック時の処理
    const handleClickChronology = (e) => {
        // レイヤー単位の click ハンドラ（features を期待）
        if (!e || !e.features || e.features.length === 0) return;

        const feature = e.features[0];
        if (!feature || !feature.properties) return;
        const featureId = feature.properties.msh_code ?? null;
        if (typeof props.setSelectMsh === 'function' && featureId != null) {
            props.setSelectMsh([featureId]);
        }
    };

    // --- クロノロジー用 GeoJSON 作成 ---
    const buildChronologyGeoJSON = (list) => {
        const fc = { type: "FeatureCollection", features: [] };
        if (!Array.isArray(list) || list.length === 0) return fc;

        const toNumber = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        };

        const features = list.map(item => {
            const lat = item.DC_LAT_NUM ?? item.dc_lat_num ?? item.lat ?? null;
            const lon = item.DC_LNG_NUM ?? item.dc_lng_num ?? item.lng ?? item.lon ?? null;
            if (lat == null || lon == null) return null;
            const coordsLon = Number(lon);
            const coordsLat = Number(lat);
            if (!Number.isFinite(coordsLon) || !Number.isFinite(coordsLat)) return null;

            return {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [coordsLon, coordsLat]
                },
                properties: {
                    code: item.code,
                    city_code: item.city_code,
                    city_name: item.city_name,
                    area_code: item.area_code,
                    area_name: item.area_name,
                    msh_code: item.msh_code,
                    tsm_recept_date_sum: item.TSM_RECEPT_DATE_SUM || item.tsm_recept_date_sum,
                    txt_act_stts_name: item.TXT_ACT_STTS_NAME || item.txt_act_stts_name ,
                    int_collapse_num: toNumber(item.INT_COLLAPSE_NUM || item.int_collapse_num),
                    int_landslide_num: toNumber(item.INT_LANDSLIDE_NUM || item.int_landslide_num),
                    int_flood_num: toNumber(item.INT_FLOOD_NUM || item.int_flood_num),
                    int_fallen_tree_num: toNumber(item.INT_FALLEN_TREE_NUM || item.int_fallen_tree_num),
                    int_power_outage_num: toNumber(item.INT_POWER_OUTAGE_NUM || item.int_power_outage_num),
                    int_water_outage_num: toNumber(item.INT_WATER_OUTAGE_NUM || item.int_water_outage_num),
                    int_other_num: toNumber(item.INT_OTHER_NUM || item.int_other_num),
                    int_human_num: toNumber(item.INT_HUMAN_NUM || item.int_human_num),
                    int_regulation_num: toNumber(item.INT_REGULATION_NUM || item.int_regulation_num),
                    int_river_burst_num: toNumber(item.INT_RIVER_BURST_NUM || item.int_river_burst_num),
                    int_all_num: toNumber(item.INT_ALL_NUM || item.int_all_num),
                    txt_main_damage_name: item.TXT_MAIN_DAMAGE_NAME || item.txt_main_damage_name,
                    int_main_damage_num: toNumber(item.INT_MAIN_DAMAGE_NUM || item.int_main_damage_num),
                    int_finished_num: toNumber(item.INT_FINISHED_NUM || item.int_finished_num),
                    int_ongoing_num: toNumber(item.INT_ONGOING_NUM || item.int_ongoing_num)
                }
            };
        }).filter(Boolean);

        fc.features = features;
        return fc;
    };

    // クロノロジースタイル変更
    const setChronologyStyle = () => {
        // データがなければ透明化して終了
        if (!checkData(chronologyDataRef.current)) {
            map.current.setPaintProperty('chronology', 'circle-color', 'rgba(0,0,0,0)');
            map.current.setPaintProperty('chronology', 'circle-stroke-color', 'rgba(0,0,0,0)');
            return;
        }

        // 塗り色を設定
        const selectedData = chronologyDataRef.current
        const matchExpression_fill = ['match', ['get', 'code']];
        const matchExpression_stroke = ['match', ['get', 'code']];

        for (const item of selectedData) {
            matchExpression_fill.push(
                item.code,
                setChronologyLevelColor(item.TXT_MAIN_DAMAGE_NAME || item.txt_main_damage_name));

            matchExpression_stroke.push(item.code, '#ffffff');
        }
        matchExpression_fill.push('rgba(0,0,0,0)');
        matchExpression_stroke.push('rgba(0,0,0,0)');

        // レイヤに適用
        map.current.setPaintProperty('chronology', 'circle-color', matchExpression_fill);
        map.current.setPaintProperty('chronology', 'circle-stroke-color', matchExpression_stroke);
    };

    const checkData = (data) => {
        if (!data || data === undefined || data === null || data.length < 1) {
            return false
        }
        return true
    }

    // 地図のセンターとズーム調整
    const setMapCenterZoomChronology = (geo) => {
        if (!map.current) return;
        if (geo.features.length === 0) return;

        // Turf.js で bbox を取得
        const bounds = bbox(geo); // [west, south, east, north]

        map.current.fitBounds(bounds, {
            padding: 30,
            maxZoom: 13,   // 拡大制限
            duration: 800  // アニメーション時間
        });
    }


    // 地図のセンターとズーム調整
    const setMapCenterZoom = (selectLayer_category, ids) => {
        if (!map.current || ids.length === 0) return;

        const features =
            (selectLayer_category === BASE_LAYER_KEY.CITY) ? choropleth_city.features
                : (selectLayer_category === BASE_LAYER_KEY.AREA) ? choropleth_area.features
                    : choropleth_msh.features;

        // 対象IDの feature を抽出
        const filteredFeatures = features.filter(f =>
            ids.includes(f.properties.id)
        );

        if (filteredFeatures.length === 0) {
            console.warn("対象フィーチャが見つかりません");
            return;
        }

        // 抽出したフィーチャのコレクションを作成
        const filteredCollection = {
            type: "FeatureCollection",
            features: filteredFeatures
        };

        // Turf.js で bbox を取得
        const bounds = bbox(filteredCollection); // [west, south, east, north]

        map.current.fitBounds(bounds, {
            padding: 30,
            maxZoom: 13,   // 拡大制限
            duration: 800  // アニメーション時間
        });
    }

    // レイヤーマウスホバー時の処理
    const handleMouseMove = (e) => {
        const feature = e.features[0];
        if (!feature) return;
        const id = feature.properties.id;
        const properties = feature.properties;
        // すでにホバー中なら無視
        if (hoveredId === id) return;

        const riskData = filterData()
        if (!riskData || riskData === undefined || riskData === null || riskData.length < 1) return

        const data = riskData.find(item => item.code === Number(id));

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }
        if (!data) {
            map.current.getCanvas().style.cursor = '';
            return;
        } else {
            map.current.getCanvas().style.cursor = 'pointer';
        }
        hoveredId = id;

        const nameStr = (props.selectLayer.category === BASE_LAYER_KEY.CITY) ? "市町村" :
            (props.selectLayer.category === BASE_LAYER_KEY.AREA) ? "字" :
                "メッシュ"

        const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false
        })
            .setLngLat(e.lngLat)
            .setHTML(`
                <div>${nameStr}：<strong>${properties.name}</strong></div>
                <div>${props.tooltipLabel}：<strong>${data.risk_val}</strong></div>
                <div>１時間雨量：<strong>${data.h1_val} mm</strong></div>
            `)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null
        }
    };

    // レイヤークリック時の処理
    const handleClick = (e, category) => {
        const feature = e.features[0];
        const featureId = feature.properties.id;
        switch (category) {
            case BASE_LAYER_KEY.CITY:
                props.setSelectSubCity([featureId])
                props.setSelectArea([])
                props.setSelectMsh([])
                props.setSelectBsn([])
                break
            case BASE_LAYER_KEY.AREA:
                props.setSelectSubCity([])
                props.setSelectArea([featureId])
                props.setSelectMsh([])
                props.setSelectBsn([])
                break
            case BASE_LAYER_KEY.MSH:
                props.setSelectSubCity([])
                props.setSelectArea([])
                props.setSelectMsh([featureId])
                props.setSelectBsn([])
                break
            default:
                props.setSelectSubCity([])
                props.setSelectArea([])
                props.setSelectMsh([])
                props.setSelectBsn([])
                break
        }
    };

    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        setLayerStyle();
        map.current.on('click', BASE_LAYER_KEY.CITY, (e) => handleClick(e, BASE_LAYER_KEY.CITY));
        map.current.on('click', BASE_LAYER_KEY.AREA, (e) => handleClick(e, BASE_LAYER_KEY.AREA));
        map.current.on('click', BASE_LAYER_KEY.MSH, (e) => handleClick(e, BASE_LAYER_KEY.MSH));
        map.current.getCanvas().addEventListener('mouseleave', handleMouseLeave);
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return;

        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.riskMapCenter,
            zoom: props.riskMapZoom,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false
        }), 'bottom-right');

        map.current.on('load', handleLoad);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);
            map.current.off('click', BASE_LAYER_KEY.CITY, (e) => handleClick(e, BASE_LAYER_KEY.CITY));
            map.current.off('click', BASE_LAYER_KEY.AREA, (e) => handleClick(e, BASE_LAYER_KEY.AREA));
            map.current.off('click', BASE_LAYER_KEY.MSH, (e) => handleClick(e, BASE_LAYER_KEY.MSH));
            map.current.off('mouseenter', BASE_LAYER_KEY.CITY, (e) => { handleMouseMove(e) });
            map.current.off('mouseenter', BASE_LAYER_KEY.AREA, (e) => { handleMouseMove(e) });
            map.current.off('mouseenter', BASE_LAYER_KEY.MSH, (e) => { handleMouseMove(e) });
            map.current.off('mousemove', BASE_LAYER_KEY.CITY, (e) => { handleMouseMove(e) });
            map.current.off('mousemove', BASE_LAYER_KEY.AREA, (e) => { handleMouseMove(e) });
            map.current.off('mousemove', BASE_LAYER_KEY.MSH, (e) => { handleMouseMove(e) });
            map.current.off('mouseleave', BASE_LAYER_KEY.CITY, handleMouseLeave);
            map.current.off('mouseleave', BASE_LAYER_KEY.AREA, handleMouseLeave);
            map.current.off('mouseleave', BASE_LAYER_KEY.MSH, handleMouseLeave);
            map.current.off('styledata', setLayerStyle);
            map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);
            map.current.remove();
            map.current = null;
        };
    }, []);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {
        selectCitysRef.current = props.selectCitys

        if (props.selectSubCity.length > 0) props.setSelectSubCity([]);
        if (props.selectArea.length > 0) props.setSelectArea([]);
        if (props.selectMsh.length > 0) props.setSelectMsh([]);
        if (props.selectBsn.length > 0) props.setSelectBsn([]);

        // 対象のデータを選ぶ
        let riskData = filterData();

        if (riskData && riskData !== undefined && riskData !== null && riskData.length > 0) {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
            }
        }
    }, [props.selectCitys]);

    useEffect(() => {
        chronologyDataRef.current = props.chronologyList

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [
        props.chronologyList,
    ]);

    useEffect(() => {
        // 更新用に ref も更新
        chronologyDataRef.current = props.chronologyList;

        if (!map.current || !map.current.isStyleLoaded()) return;

        const geo = buildChronologyGeoJSON(props.chronologyList || []);

        // ソースが存在すればデータをセット、なければ作る
        try {
            if (map.current.getSource && map.current.getSource('chronology')) {
                map.current.getSource('chronology').setData(geo);
            } else {
                map.current.addSource('chronology', {
                    type: 'geojson',
                    data: geo
                });
                // レイヤーが存在しなければ addLayer する（既に追加済みならスキップ）
                if (!map.current.getLayer || !map.current.getLayer('chronology')) {
                    map.current.addLayer({
                        id: 'chronology',
                        type: 'circle',
                        source: 'chronology',
                        paint: {
                            'circle-radius': [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                5, 6,
                                10, 8,
                                12, 10,
                                15, 12,
                                18, 14
                            ],
                            'circle-stroke-width': 1.0,
                            'circle-stroke-color': '#ffffff',
                            'circle-color': '#0000FF',
                            'circle-opacity': 1
                        },
                        layout: { visibility: 'visible' }
                    });
                }
            }
        } catch (err) {
            console.error('update chronology source error:', err);
        }

        // 色分けや stroke の更新
        try {
            setChronologyStyle();
        } catch (err) {
            console.error('setChronologyStyle error:', err);
        }

        // 表示用に中心とズームも調整（要素があれば）
        if (geo && geo.features && geo.features.length > 0) {
            setMapCenterZoomChronology(geo);
        }
    }, [props.chronologyList]);

    // レイヤー切り替え時
    useEffect(() => {
        if (props.shouldClearSelect) {
            props.setSelectSubCity([])
            props.setSelectArea([]);
            props.setSelectMsh([]);
            props.setSelectBsn([]);
        } else {
            setLayerStyle();
        }
        props.setShouldClearSelect(false);
    }, [props.selectLayer]);

    // selectLayerList（クロノロジーチェックボックス）の変更を監視
    useEffect(() => {
        if (!map.current || !map.current.isStyleLoaded()) return;
        if (!map.current.getLayer('chronology')) return;

        if (props.selectLayerList?.chronology) {
            map.current.setLayoutProperty('chronology', 'visibility', 'visible');
        } else {
            map.current.setLayoutProperty('chronology', 'visibility', 'none');
        }
    }, [props.selectLayerList]);

    // データ更新時
    // 市町村チェックボックス変更時、
    // レイヤー切り替え時
    // 市町村レイヤークリック時
    // 字レイヤークリック時
    // メッシュレイヤークリック時
    // リセットボタンで個別の選択が解除されたときも呼ばれる
    useEffect(() => {
        cityLandslideRiskH1Ref.current = props.cityLandslideRiskH1
        areaLandslideRiskH1Ref.current = props.areaLandslideRiskH1
        mshLandslideRiskH1Ref.current = props.mshLandslideRiskH1

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                if (map.current.getLayer(props.selectLayer.category)) {
                    setLayerStyle();
                    clearInterval(interval);
                }
            }
        }, 100);

        return () => clearInterval(interval);
    }, [
        props.cityLandslideRiskH1, props.areaLandslideRiskH1, props.mshLandslideRiskH1,
        props.selectSubCity, props.selectArea, props.selectMsh, props.chooseNum
    ]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <MapWrapper>
                <div
                    ref={mapContainer}
                    className=""
                    style={mapStyle} />
                <MapFullscreenIcon
                    fullscreenHandle={fullscreenHandle}
                    top={'70px'}
                />
                <MapSwitchLayerIcon
                    selectLayer={props.selectLayer}
                    setSelectLayer={props.setSelectLayer}
                    setShouldClearSelect={props.setShouldClearSelect}
                    pageType={props.pageType}
                    selectLayerList={props.selectLayerList}
                    setSelectLayerList={props.setSelectLayerList}
                />
                <MapSedimentLegendIcon
                    label={props.legendLabel}
                />
            </MapWrapper>
        </StyleFullScreen>
    );
};

export default MapComponent;